import type { DataTableColumnDef } from '@neo/blocks/cx-data-table'
import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import { Typography } from '@neo/primitives/typography'
import { pluralize } from '@shared/utils'
import type { RUserSession } from 'v2/types/api/auth'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { getAccountIdentifier } from '../utils/account-identifier'
import { getIntegrationStatusOptions } from '../utils/integration-availability'
import { AppLogoMark } from './catalog-row'
import type { ExternalAppRow } from './external-app-actions'
import { connections, rollUpConnectedOn } from './external-apps-rollup'
import { ToolActionsMenu, type ToolMenuAction } from './tool-actions-menu'

export interface ToolActionsColumnProps {
  currentUser?: RUserSession
  canEdit: boolean
  canDelete: boolean
  onAction: (action: ToolMenuAction, targets: ExternalAppRow[]) => void
}

export const getColumns = (
  currentUser?: RUserSession,
  aciEnabled = false,
  toolActions?: ToolActionsColumnProps,
): DataTableColumnDef<ExternalAppRow>[] => [
  // Column sizes are proportions, not pixels: the table stretches and shares out slack by ratio.
  {
    accessorKey: 'name',
    header: 'Tool',
    size: 360,
    filterFn: (row, _, filterValue) => {
      const name = row.original.name?.toLowerCase() || ''
      return name.includes(filterValue?.toLowerCase())
    },
    Cell: ({ row }) => {
      const { isGroup } = row.original

      if (isGroup) {
        return (
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 p-2 bg-bg3 rounded-md">
              <AppLogoMark app={row.original} />
            </div>
            <Typography variant="S/500">{row.original.name}</Typography>
          </div>
        )
      }

      return (
        <div className="flex items-center gap-2 ml-4">
          <div>
            <Typography variant="S/400"> {getAccountIdentifier(row.original)}</Typography>
            {row.original.provider === 'slack' &&
              typeof row.original.account_identifier?.team === 'string' && (
                <Typography variant="XS/400" className="text-secondary">
                  {row.original.account_identifier.team}
                </Typography>
              )}
          </div>
        </div>
      )
    },
  },
  {
    id: 'accounts',
    header: 'Connected accounts',
    // No Status or Owner column: both are per-account facts, and a tool can hold several.
    size: 200,
    Cell: ({ row }) =>
      row.original.isGroup ? (
        <Typography variant="S/400" data-testid="text-connected-accounts">
          {pluralize(connections(row.original).length, 'account')}
        </Typography>
      ) : null,
  },
  {
    accessorKey: 'created_on',
    header: 'Created On',
    size: 220,
    Cell: ({ row }) => {
      const { isGroup } = row.original
      // Most recent connection, rather than an arbitrary one of the provider's accounts.
      const date = isGroup ? rollUpConnectedOn(row.original) : row.original.created_on
      if (!date) return null

      return <Typography variant="S/400">{dayjs(date).format('MMM D, YYYY')}</Typography>
    },
  },
  {
    id: 'actions',
    header: '',
    size: 56,
    Cell: ({ row }) => {
      if (!toolActions || !row.original.isGroup) return null

      return (
        <div className="flex justify-end">
          <ToolActionsMenu
            row={row.original}
            currentUser={currentUser}
            aciEnabled={aciEnabled}
            canEdit={toolActions.canEdit}
            canDelete={toolActions.canDelete}
            onAction={toolActions.onAction}
          />
        </div>
      )
    },
  },
]

export const getFilterFields = (aciEnabled = false): DataTableFilterField<RExternalOrgApp>[] => [
  {
    label: 'Name',
    value: 'name',
    paramKey: 'connection_details',
    placeholder: 'Search',
  },
  {
    label: 'Status',
    value: 'state',
    paramKey: 'state',
    options: getIntegrationStatusOptions(aciEnabled),
  },
]

// Transform flat app data into grouped structure
export const buildGroupedTableRows = (apps: RExternalOrgApp[]): ExternalAppRow[] => {
  // Group apps by provider
  const grouped = apps.reduce(
    (acc, app) => {
      const provider = app.provider || app.name
      if (!acc[provider]) {
        acc[provider] = []
      }
      acc[provider].push(app)
      return acc
    },
    {} as Record<string, RExternalOrgApp[]>,
  )

  // Transform into grouped rows
  return Object.entries(grouped).map(([provider, providerApps]) => {
    const firstApp = providerApps[0]

    const groupRow: ExternalAppRow = {
      ...firstApp, // Include all properties from RExternalOrgApp
      id: `group-${provider}`,
      name: firstApp.name,
      state: '',
      created_on: '',
      provider,
      isGroup: true,
      subRows: providerApps.map((app) => ({
        ...app, // Include all original app properties first
        provider: app.provider || app.name,
        isGroup: false,
      })),
    }

    return groupRow
  })
}
